<script setup>
import {
  computed,
  useSlots,
} from 'vue';
import { useI18n } from 'vue-i18n';

const slots = useSlots();
const { t } = useI18n();
const props = defineProps({
  label: {
    type: String,
    required: false,
  },
  rawLabel: {
    type: String,
    required: false,
    default: null,
  },
  badgeStyle: {
    type: String,
    default: 'primary',
  },
  icon: {
    type: String,
    required: false,
  },
  // Path under @/assets/icons, e.g. "similar-items/sm-dark.svg". Takes
  // precedence over `icon`, same as in GenericButton.
  iconImage: {
    type: String,
    required: false,
  },
  small: {
    type: Boolean,
    default: false,
  },
  reverse: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    required: false,
  },
  rawTooltip: {
    type: String,
    required: false,
  },
  tooltipPlacement: {
    type: String,
    default: 'top',
  }
});

const isSafeIconPath = (iconPath) => (
  /^[a-zA-Z0-9/_-]+\.[a-zA-Z0-9]+$/.test(iconPath)
  && !iconPath.includes('..')
  && !iconPath.startsWith('/')
);

const iconImageSrc = computed(() => {
  if (!props.iconImage || !isSafeIconPath(props.iconImage)) {
    return null;
  }
  try {
    return require(`@/assets/icons/${props.iconImage}`);
  } catch {
    return null;
  }
});

const iconMarginClass = computed(() => {
  if (!props.label && !props.rawLabel && !slots.default) {
    return '';
  }
  return props.reverse ? 'ms-2' : 'me-2';
});

const iconClass = computed(
  () => [
    `fa-${props.icon}`,
    iconMarginClass.value,
  ],
)

const badgeClass = computed(() => {
  return [
    `badge-${props.badgeStyle}`,
    props.small ? 'badge-sm' : '',
  ];
});

const computedTooltip = computed(() => {
  if (!props.tooltip && !props.rawTooltip) {
    return null;
  }
  const params = {
    placement: props.tooltipPlacement,
  };
  if (props.rawTooltip) {
    params.label = props.rawTooltip;
  } else if (props.tooltip) {
    params.label = t(props.tooltip);
  }
  return params;
});
</script>

<template>
  <div
    class="badge "
    :class="badgeClass"
    v-tooltip="computedTooltip"
  >
    <div
      class="d-flex align-items-center justify-content-center"
      :class="{'text-nowrap': !icon && !iconImageSrc, 'flex-row-reverse': reverse}"
    >
      <img
        v-if="iconImageSrc"
        class="badge-icon-image"
        :class="iconMarginClass"
        :src="iconImageSrc"
        alt=""
      >
      <i
        v-else-if="icon"
        class="fa"
        :class="iconClass"
      />

      <span v-if="label">{{ $t(label) }}</span>
      <span v-else-if="rawLabel !== null">{{ rawLabel }}</span>
      <slot v-else></slot>
    </div>
  </div>
</template>

<style scoped>
.badge-icon-image {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
</style>
